import React, { useState, useRef, useEffect, useCallback } from "react";
import { Plus, X, Calendar, Award, Music, ChevronLeft, Play, Pause, Volume2, VolumeX, Repeat, Lamp, Sparkles, Heart, Star, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { axiosAuth } from "../shared/axiosAuth";
import { API, GRATITUDE_PROMPTS, GUIDED_JOURNAL_PROMPTS } from "../data/constants";

// Milestone definitions - gentle celebrations
const MILESTONES = [
  { days: 1, message: "You took the first step! 🌱", celebration: "Welcome to your journaling journey!" },
  { days: 7, message: "One week of reflection! 🌟", celebration: "A whole week of gentle moments with yourself." },
  { days: 14, message: "Two weeks strong! 💫", celebration: "You're building a beautiful habit." },
  { days: 30, message: "A month of memories! 🏆", celebration: "30 days of caring for your heart and mind." },
  { days: 60, message: "Two months of growth! 🌈", celebration: "Your dedication to self-care is inspiring." },
  { days: 100, message: "100 days! Incredible! 👑", celebration: "A hundred days of honoring your journey." },
  { days: 365, message: "One year! Legendary! 🎊", celebration: "A full year of beautiful reflections." }
];

// Simple Confetti Component
const Confetti = ({ active, onComplete }) => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    if (active) {
      // Create confetti particles
      const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        color: colors[Math.floor(Math.random() * colors.length)],
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 2,
        size: 8 + Math.random() * 8,
        rotation: Math.random() * 360
      }));
      setParticles(newParticles);
      
      // Clear after animation
      const timer = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [active, onComplete]);
  
  if (!active || particles.length === 0) return null;
  
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${p.x}%`,
            top: '-20px',
            width: `${p.size}px`,
            height: `${p.size}px`,
            backgroundColor: p.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            transform: `rotate(${p.rotation}deg)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti-fall {
          animation: confetti-fall ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// Milestone Celebration Modal
const MilestoneCelebration = ({ milestone, onClose }) => {
  if (!milestone) return null;
  
  return (
    <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center animate-bounce-in">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="font-heading text-2xl font-bold text-charcoal mb-2">{milestone.message}</h2>
        <p className="text-sage text-lg mb-4">{milestone.celebration}</p>
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-4 mb-6">
          <p className="text-amber-700 font-medium">
            Remember: There's no pressure here. Every entry matters, whether it's your first or your hundredth. 
            This is your gentle space. 💕
          </p>
        </div>
        <button
          onClick={onClose}
          className="bg-sage text-white px-8 py-3 rounded-full font-medium hover:bg-sage-dark transition-all"
        >
          Continue Journaling 🌱
        </button>
        <style>{`
          @keyframes bounce-in {
            0% { transform: scale(0.5); opacity: 0; }
            70% { transform: scale(1.05); }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-bounce-in {
            animation: bounce-in 0.4s ease-out forwards;
          }
        `}</style>
      </div>
    </div>
  );
};

// Helper to manage favorite prompts in localStorage
const FAVORITES_KEY = 'pawsHarmonyFavoritePrompts';
const CELEBRATED_KEY = 'pawsHarmonyCelebratedMilestones';

const getFavoritePrompts = () => {
  try {
    const saved = localStorage.getItem(FAVORITES_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};
const saveFavoritePrompts = (favorites) => {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
};

// Track which milestones have been celebrated
const getCelebratedMilestones = () => {
  try {
    const saved = localStorage.getItem(CELEBRATED_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};
const markMilestoneCelebrated = (days) => {
  const celebrated = getCelebratedMilestones();
  if (!celebrated.includes(days)) {
    celebrated.push(days);
    localStorage.setItem(CELEBRATED_KEY, JSON.stringify(celebrated));
  }
};

// Calculate journaling streak from entries
const calculateStreak = (moodEntries, gratitudeEntries, stories) => {
  // Collect all journal dates
  const allDates = new Set();
  
  moodEntries?.forEach(e => {
    if (e.date) allDates.add(e.date.split('T')[0]);
  });
  gratitudeEntries?.forEach(e => {
    if (e.date) allDates.add(e.date.split('T')[0]);
  });
  stories?.forEach(e => {
    if (e.created_at) allDates.add(e.created_at.split('T')[0]);
  });
  
  if (allDates.size === 0) return { streak: 0, totalDays: 0, journaledToday: false };
  
  // Sort dates in descending order
  const sortedDates = Array.from(allDates).sort().reverse();
  
  // Check if user journaled today
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const journaledToday = sortedDates[0] === today;
  
  // Calculate streak
  let streak = 0;
  let checkDate = journaledToday ? today : yesterday;
  
  // If most recent entry is older than yesterday, streak is 0
  if (!journaledToday && sortedDates[0] !== yesterday) {
    return { streak: 0, totalDays: allDates.size, journaledToday: false };
  }
  
  for (let i = 0; i < sortedDates.length; i++) {
    if (sortedDates[i] === checkDate) {
      streak++;
      // Move to previous day
      const prevDate = new Date(checkDate);
      prevDate.setDate(prevDate.getDate() - 1);
      checkDate = prevDate.toISOString().split('T')[0];
    } else if (sortedDates[i] < checkDate) {
      // Gap found, streak ends
      break;
    }
  }
  
  return { streak, totalDays: allDates.size, journaledToday };
};

// Streak messages based on streak length - gentle and encouraging, never pressuring
const getStreakMessage = (streak, journaledToday) => {
  if (!journaledToday && streak === 0) return "Whenever you're ready, this space is here for you ✨";
  if (!journaledToday) return "Your journal is here whenever you need it 💕";
  if (streak === 1) return "A beautiful beginning! 🌱";
  if (streak < 7) return "You're nurturing something wonderful 🌿";
  if (streak < 14) return "One week of gentle moments 🌟";
  if (streak < 30) return "Your dedication warms the heart 💫";
  if (streak < 60) return "A month of caring for yourself 🏆";
  if (streak < 100) return "Your journey is truly inspiring 👑";
  return "A remarkable practice of self-love 🌈✨";
};

// Streak Display Component
const StreakDisplay = ({ streak, totalDays, journaledToday }) => {
  const message = getStreakMessage(streak, journaledToday);
  
  // Flame size grows with streak
  const getFlameEmoji = () => {
    if (streak === 0) return "🕯️";
    if (streak < 3) return "🔥";
    if (streak < 7) return "🔥🔥";
    if (streak < 14) return "🔥🔥🔥";
    if (streak < 30) return "💥🔥💥";
    return "⭐🔥⭐";
  };

  return (
    <div className="bg-gradient-to-r from-orange-100 via-amber-50 to-yellow-100 rounded-2xl p-4 mb-6 border border-amber-200 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="text-center">
          <div className="text-3xl">{getFlameEmoji()}</div>
        </div>
        <div>
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-3xl font-bold text-amber-600">{streak}</span>
            <span className="text-amber-700 font-medium">day streak</span>
          </div>
          <p className="text-amber-600/80 text-sm">{message}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-amber-700/60 text-xs">Total journal days</p>
        <p className="font-heading text-xl font-semibold text-amber-600">{totalDays}</p>
        {journaledToday && (
          <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full mt-1">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            Journaled today
          </span>
        )}
      </div>
    </div>
  );
};

// Mini Audio Player for paired songs
const MiniAudioPlayer = ({ audioFile, songTitle }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="flex items-center gap-2 bg-sage/10 rounded-full px-3 py-1.5 mt-2">
      <audio ref={audioRef} src={audioFile} onEnded={() => setIsPlaying(false)} />
      <button onClick={togglePlay} className="w-6 h-6 bg-sage text-white rounded-full flex items-center justify-center hover:bg-sage-dark transition-all">
        {isPlaying ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
      </button>
      <span className="text-xs text-sage font-medium truncate max-w-[150px]">{songTitle}</span>
      <button onClick={toggleMute} className="text-sage hover:text-sage-dark transition-all">
        {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
      </button>
    </div>
  );
};

// Guided Prompt Card Component
const PromptCard = ({ prompt, onClick, isFavorite, onToggleFavorite }) => (
  <div
    data-testid={`prompt-card-${prompt.id}`}
    className={`bg-gradient-to-br ${prompt.color} rounded-2xl p-5 text-left hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 ${prompt.borderColor} group relative`}
  >
    {/* Favorite Star Button */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggleFavorite(prompt.id);
      }}
      data-testid={`favorite-${prompt.id}`}
      className={`absolute top-3 right-3 p-1.5 rounded-full transition-all ${
        isFavorite 
          ? 'text-amber-500 bg-amber-100 hover:bg-amber-200' 
          : 'text-stone/40 hover:text-amber-400 hover:bg-white/50 opacity-0 group-hover:opacity-100'
      }`}
      title={isFavorite ? "Remove from favorites" : "Add to favorites"}
    >
      <Star size={16} fill={isFavorite ? "currentColor" : "none"} />
    </button>
    
    <button
      onClick={onClick}
      className="w-full text-left"
    >
      <div className="flex items-start gap-3 pr-8">
        <span className="text-3xl group-hover:scale-110 transition-transform">{prompt.icon}</span>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-charcoal mb-1">{prompt.title}</h3>
          <p className="text-stone text-sm">{prompt.description}</p>
        </div>
      </div>
    </button>
  </div>
);

// Guided Writing Modal
const GuidedWritingModal = ({ prompt, onClose, onSave, pets, songs, loading }) => {
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [selectedPet, setSelectedPet] = useState("");
  const [selectedSong, setSelectedSong] = useState("");

  const handleSave = () => {
    if (!content.trim()) {
      toast.error("Please write something before saving");
      return;
    }
    onSave({
      title: title || prompt.title,
      content,
      pet_id: selectedPet || null,
      paired_song_id: selectedSong || null,
      prompt_used: prompt.id
    });
  };

  const selectedSongData = songs.find(s => s.id === selectedSong);

  return (
    <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{prompt.icon}</span>
            <div>
              <h2 className="font-heading text-2xl font-semibold text-charcoal">{prompt.title}</h2>
              <p className="text-stone text-sm">{prompt.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-stone hover:text-charcoal p-2">
            <X size={24} />
          </button>
        </div>

        {/* The Prompt */}
        <div className={`bg-gradient-to-br ${prompt.color} rounded-2xl p-4 mb-6 border ${prompt.borderColor}`}>
          <p className="font-handwritten text-lg text-charcoal italic flex items-start gap-2">
            <Lamp size={20} className="text-amber-500 flex-shrink-0 mt-1" />
            {prompt.prompt}
          </p>
        </div>

        <div className="space-y-4">
          {/* Optional Title */}
          <div>
            <label className="block text-charcoal font-medium mb-2">Give it a title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none"
              placeholder={prompt.title}
            />
          </div>

          {/* Writing Area */}
          <div>
            <label className="block text-charcoal font-medium mb-2">Your reflection</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-48 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-3 outline-none resize-none font-handwritten text-lg"
              placeholder="Take your time... let the words flow..."
              autoFocus
            />
          </div>

          {/* Pet Selection */}
          {pets.length > 0 && (
            <div>
              <label className="block text-charcoal font-medium mb-2">About which pet? (optional)</label>
              <select
                value={selectedPet}
                onChange={(e) => setSelectedPet(e.target.value)}
                className="w-full h-12 border-2 border-gray-200 rounded-xl px-4 outline-none"
              >
                <option value="">Choose a pet...</option>
                {pets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          {/* Song Pairing */}
          {songs.length > 0 && (
            <div>
              <label className="block text-charcoal font-medium mb-2 flex items-center gap-2">
                <Music size={18} className="text-sage" />
                Pair with a song (optional)
              </label>
              <select
                value={selectedSong}
                onChange={(e) => setSelectedSong(e.target.value)}
                className="w-full h-12 border-2 border-gray-200 rounded-xl px-4 outline-none"
              >
                <option value="">Choose a song to remember this by...</option>
                {songs.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
              {selectedSongData && selectedSongData.audio_file && (
                <div className="mt-2 bg-sage/5 rounded-xl p-3">
                  <p className="text-xs text-stone mb-2">Preview your paired song:</p>
                  <MiniAudioPlayer audioFile={selectedSongData.audio_file} songTitle={selectedSongData.title} />
                </div>
              )}
              <p className="text-xs text-stone mt-2">As life evolves, you can always change the paired song later.</p>
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full bg-sage text-white py-4 rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? "Saving..." : (
              <>
                <Heart size={18} />
                Save to My Journal
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const JournalPage = ({ moodEntries, fetchMoodEntries, gratitudeEntries, fetchGratitude, stories, fetchStories, milestones, fetchMilestones, pets, songs = [] }) => {
  const [activeTab, setActiveTab] = useState("prompts");
  const [showGratitudeForm, setShowGratitudeForm] = useState(false);
  const [showStoryForm, setShowStoryForm] = useState(false);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [showGuidedModal, setShowGuidedModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [gratitudeText, setGratitudeText] = useState("");
  const [gratitudePet, setGratitudePet] = useState("");
  const [gratitudeSong, setGratitudeSong] = useState("");
  const [storyData, setStoryData] = useState({ title: "", content: "", pet_id: "", paired_song_id: "" });
  const [milestoneData, setMilestoneData] = useState({ pet_id: "", title: "", description: "", milestone_date: "", milestone_type: "other" });
  const [loading, setLoading] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [favoritePrompts, setFavoritePrompts] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [celebrationMilestone, setCelebrationMilestone] = useState(null);
  const [prevStreak, setPrevStreak] = useState(null);

  // Calculate journaling streak
  const streakData = calculateStreak(moodEntries, gratitudeEntries, stories);

  // Check for milestone celebrations when streak changes
  useEffect(() => {
    // Only trigger if streak increased (not on initial load or decrease)
    if (prevStreak !== null && streakData.streak > prevStreak) {
      const celebrated = getCelebratedMilestones();
      const milestone = MILESTONES.find(m => m.days === streakData.streak && !celebrated.includes(m.days));
      
      if (milestone) {
        // Delay to let the save complete visually
        setTimeout(() => {
          setShowConfetti(true);
          setCelebrationMilestone(milestone);
          markMilestoneCelebrated(milestone.days);
        }, 500);
      }
    }
    setPrevStreak(streakData.streak);
  }, [streakData.streak, prevStreak]);

  const handleCloseCelebration = useCallback(() => {
    setCelebrationMilestone(null);
  }, []);

  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
  }, []);

  // Load favorites from localStorage on mount
  useEffect(() => {
    setFavoritePrompts(getFavoritePrompts());
  }, []);

  const toggleFavoritePrompt = (promptId) => {
    setFavoritePrompts(prev => {
      const newFavorites = prev.includes(promptId)
        ? prev.filter(id => id !== promptId)
        : [...prev, promptId];
      saveFavoritePrompts(newFavorites);
      toast.success(
        prev.includes(promptId) 
          ? "Removed from favorites" 
          : "Added to favorites ⭐"
      );
      return newFavorites;
    });
  };

  // Separate prompts into favorites and others
  const favoritePromptsList = GUIDED_JOURNAL_PROMPTS.filter(p => favoritePrompts.includes(p.id));
  const otherPromptsList = GUIDED_JOURNAL_PROMPTS.filter(p => !favoritePrompts.includes(p.id));

  const gratitudePrompt = GRATITUDE_PROMPTS[Math.floor(Math.random() * GRATITUDE_PROMPTS.length)];
  const milestoneTypes = [{ value: "birthday", emoji: "🎂" }, { value: "gotcha_day", emoji: "💕" }, { value: "first_walk", emoji: "🚶" }, { value: "training", emoji: "🎓" }, { value: "health", emoji: "💊" }, { value: "other", emoji: "⭐" }];

  const handleOpenGuidedPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setShowGuidedModal(true);
  };

  const handleSaveGuidedEntry = async (data) => {
    setLoading(true);
    try {
      await axiosAuth.post(`${API}/stories`, data);
      toast.success("Journal entry saved! 📖");
      await fetchStories();
      setShowGuidedModal(false);
      setSelectedPrompt(null);
    } catch (error) {
      toast.error("Failed to save journal entry");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGratitude = async () => { 
    if (!gratitudeText.trim()) return; 
    setLoading(true); 
    try { 
      await axiosAuth.post(`${API}/gratitude`, { 
        text: gratitudeText, 
        pet_id: gratitudePet || null,
        paired_song_id: gratitudeSong || null
      }); 
      toast.success("Gratitude saved! 🙏"); 
      await fetchGratitude(); 
      setGratitudeText(""); 
      setGratitudePet(""); 
      setGratitudeSong("");
      setShowGratitudeForm(false); 
    } catch (error) { 
      toast.error("Failed to save gratitude"); 
    } finally { 
      setLoading(false); 
    } 
  };

  const handleSaveStory = async () => { 
    if (!storyData.title.trim() || !storyData.content.trim()) { 
      toast.error("Title and content required"); 
      return; 
    } 
    setLoading(true); 
    try { 
      if (editingStory) {
        await axiosAuth.put(`${API}/stories/${editingStory.id}`, storyData);
        toast.success("Story updated! 📖");
      } else {
        await axiosAuth.post(`${API}/stories`, storyData);
        toast.success("Story saved! 📖");
      }
      await fetchStories(); 
      setStoryData({ title: "", content: "", pet_id: "", paired_song_id: "" }); 
      setShowStoryForm(false);
      setEditingStory(null);
    } catch (error) { 
      toast.error("Failed to save story"); 
    } finally { 
      setLoading(false); 
    } 
  };

  const handleEditStory = (story) => {
    setStoryData({
      title: story.title,
      content: story.content,
      pet_id: story.pet_id || "",
      paired_song_id: story.paired_song_id || ""
    });
    setEditingStory(story);
    setShowStoryForm(true);
  };

  const handleSaveMilestone = async () => { 
    if (!milestoneData.pet_id || !milestoneData.title.trim() || !milestoneData.milestone_date) { 
      toast.error("Pet, title and date required"); 
      return; 
    } 
    setLoading(true); 
    try { 
      await axiosAuth.post(`${API}/milestones`, milestoneData); 
      toast.success("Milestone saved! 🎉"); 
      await fetchMilestones(); 
      setMilestoneData({ pet_id: "", title: "", description: "", milestone_date: "", milestone_type: "other" }); 
      setShowMilestoneForm(false); 
    } catch (error) { 
      toast.error("Failed to save milestone"); 
    } finally { 
      setLoading(false); 
    } 
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const getPetName = (id) => pets.find(p => p.id === id)?.name || "";
  const getSongById = (id) => songs.find(s => s.id === id);
  const getPromptTitle = (promptId) => GUIDED_JOURNAL_PROMPTS.find(p => p.id === promptId)?.title || "";

  return (
    <div data-testid="journal-page" className="fade-in relative z-10">
      {/* Confetti Animation */}
      <Confetti active={showConfetti} onComplete={handleConfettiComplete} />
      
      {/* Milestone Celebration Modal */}
      <MilestoneCelebration milestone={celebrationMilestone} onClose={handleCloseCelebration} />

      <h1 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">Journal 📔</h1>
      <p className="font-handwritten text-2xl text-sage mb-6">A gentle space for reflection, gratitude, and memories 💕</p>

      {/* Journaling Streak Display */}
      <StreakDisplay 
        streak={streakData.streak} 
        totalDays={streakData.totalDays} 
        journaledToday={streakData.journaledToday} 
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {[
          { id: "prompts", label: "Guided", emoji: "🏮" },
          { id: "moods", label: "Moods", emoji: "😊" },
          { id: "gratitude", label: "Gratitude", emoji: "🙏" },
          { id: "stories", label: "Stories", emoji: "📖" },
          { id: "milestones", label: "Milestones", emoji: "🎉" }
        ].map((tab) => (
          <button key={tab.id} data-testid={`journal-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={`px-5 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${activeTab === tab.id ? "bg-sage text-white" : "bg-white/90 text-charcoal hover:bg-sage/10"}`}>{tab.emoji} {tab.label}</button>
        ))}
      </div>

      {/* Guided Prompts Tab - NEW */}
      {activeTab === "prompts" && (
        <div>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-6 mb-8 border border-amber-200">
            <div className="flex items-start gap-4">
              <Lamp size={32} className="text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="font-heading text-xl font-semibold text-charcoal mb-2">Guided Journal Prompts</h2>
                <p className="text-stone">Sometimes the hardest part is knowing where to start. Choose a lantern below to light your way — each one holds a gentle question to guide your reflection. Take your time; there's no rush. 🕯️</p>
                <p className="text-stone/70 text-sm mt-2">Tip: Hover over a card and click the ⭐ to add it to your favorites!</p>
              </div>
            </div>
          </div>

          {/* Surprise Me Button */}
          <div className="flex justify-center mb-8">
            <button
              onClick={() => {
                const randomPrompt = GUIDED_JOURNAL_PROMPTS[Math.floor(Math.random() * GUIDED_JOURNAL_PROMPTS.length)];
                toast(`✨ "${randomPrompt.title}" chosen for you!`, { duration: 2000 });
                setTimeout(() => handleOpenGuidedPrompt(randomPrompt), 300);
              }}
              data-testid="surprise-me-button"
              className="group bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 text-white px-8 py-4 rounded-full font-medium text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-3"
            >
              <span className="text-2xl group-hover:animate-bounce">🎲</span>
              Surprise Me!
              <span className="text-2xl group-hover:animate-bounce">✨</span>
            </button>
          </div>

          {/* Favorite Prompts Section */}
          {favoritePromptsList.length > 0 && (
            <div className="mb-8">
              <h3 className="font-heading text-lg font-semibold text-charcoal mb-4 flex items-center gap-2">
                <Star size={18} className="text-amber-500" fill="currentColor" />
                Your Favorite Prompts
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favoritePromptsList.map((prompt) => (
                  <PromptCard 
                    key={prompt.id} 
                    prompt={prompt} 
                    onClick={() => handleOpenGuidedPrompt(prompt)}
                    isFavorite={true}
                    onToggleFavorite={toggleFavoritePrompt}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Other Prompts */}
          {otherPromptsList.length > 0 && (
            <div>
              {favoritePromptsList.length > 0 && (
                <h3 className="font-heading text-lg font-semibold text-charcoal mb-4 flex items-center gap-2">
                  <Lamp size={18} className="text-amber-500" />
                  All Prompts
                </h3>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {otherPromptsList.map((prompt) => (
                  <PromptCard 
                    key={prompt.id} 
                    prompt={prompt} 
                    onClick={() => handleOpenGuidedPrompt(prompt)}
                    isFavorite={false}
                    onToggleFavorite={toggleFavoritePrompt}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recent Guided Entries */}
          {stories.filter(s => s.prompt_used).length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading text-xl font-semibold text-charcoal mb-4 flex items-center gap-2">
                <Sparkles size={20} className="text-amber-500" />
                Your Recent Guided Reflections
              </h3>
              <div className="space-y-4">
                {stories.filter(s => s.prompt_used).slice(0, 5).map((s) => {
                  const promptData = GUIDED_JOURNAL_PROMPTS.find(p => p.id === s.prompt_used);
                  const pairedSong = getSongById(s.paired_song_id);
                  return (
                    <div key={s.id} className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{promptData?.icon || "📖"}</span>
                        <div className="flex-1">
                          <h4 className="font-heading text-lg font-semibold text-charcoal">{s.title}</h4>
                          {promptData && <p className="text-sage text-sm mb-2">From: {promptData.title}</p>}
                          <p className="text-charcoal whitespace-pre-wrap">{s.content}</p>
                          <div className="flex items-center gap-3 mt-3 text-sm text-stone">
                            <Calendar size={14} />{formatDate(s.created_at)}
                            {s.pet_id && <span className="text-sage">• About {getPetName(s.pet_id)}</span>}
                          </div>
                          {pairedSong && pairedSong.audio_file && (
                            <MiniAudioPlayer audioFile={pairedSong.audio_file} songTitle={pairedSong.title} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Moods Tab */}
      {activeTab === "moods" && (
        <div>
          {moodEntries.length === 0 ? (
            <div className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-3xl border border-white/50">
              <span className="text-5xl block mb-4">😊</span>
              <h3 className="font-heading text-xl text-charcoal mb-2">No mood entries yet</h3>
              <p className="text-stone">Use the mood selector to track how you feel!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {moodEntries.map((e) => (
                <div key={e.id} className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50">
                  <div className="flex items-start gap-4">
                    <span className="text-5xl">{e.mood_emoji}</span>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-semibold text-charcoal">Feeling {e.mood_name}</h3>
                      <p className="text-stone text-sm flex items-center gap-1 mt-1"><Calendar size={14} />{formatDate(e.date)}</p>
                      {e.note && <p className="text-charcoal bg-sage/5 rounded-xl p-4 mt-3 font-handwritten text-lg">"{e.note}"</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Gratitude Tab */}
      {activeTab === "gratitude" && (
        <div>
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 mb-6 border border-white/50">
            <h3 className="font-heading text-xl font-semibold text-charcoal mb-4">Daily Gratitude 🙏</h3>
            {!showGratitudeForm ? (
              <button onClick={() => setShowGratitudeForm(true)} className="w-full bg-sage/10 hover:bg-sage/20 text-sage py-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2">
                <Plus size={20} />Add Today's Gratitude
              </button>
            ) : (
              <div className="space-y-4">
                <p className="text-stone italic">💭 {gratitudePrompt}</p>
                <textarea value={gratitudeText} onChange={(e) => setGratitudeText(e.target.value)} className="w-full h-24 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-3 outline-none resize-none" placeholder="What are you grateful for today?" />
                {pets.length > 0 && (
                  <select value={gratitudePet} onChange={(e) => setGratitudePet(e.target.value)} className="w-full h-12 border-2 border-gray-200 rounded-xl px-4 outline-none">
                    <option value="">About which pet? (optional)</option>
                    {pets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
                {songs.length > 0 && (
                  <div>
                    <select value={gratitudeSong} onChange={(e) => setGratitudeSong(e.target.value)} className="w-full h-12 border-2 border-gray-200 rounded-xl px-4 outline-none">
                      <option value="">Pair with a song? (optional)</option>
                      {songs.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                    </select>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={handleSaveGratitude} disabled={loading} className="flex-1 bg-sage text-white py-3 rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50">{loading ? "Saving..." : "Save 🙏"}</button>
                  <button onClick={() => setShowGratitudeForm(false)} className="px-6 py-3 text-stone hover:text-charcoal">Cancel</button>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4">
            {gratitudeEntries.map((e) => {
              const pairedSong = getSongById(e.paired_song_id);
              return (
                <div key={e.id} className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50">
                  <p className="font-handwritten text-xl text-charcoal">🙏 "{e.text}"</p>
                  <div className="flex items-center gap-3 mt-3 text-sm text-stone">
                    <Calendar size={14} />{formatDate(e.date)}
                    {e.pet_id && <span className="text-sage">• About {getPetName(e.pet_id)}</span>}
                  </div>
                  {pairedSong && pairedSong.audio_file && (
                    <MiniAudioPlayer audioFile={pairedSong.audio_file} songTitle={pairedSong.title} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stories Tab */}
      {activeTab === "stories" && (
        <div>
          <button onClick={() => { setEditingStory(null); setStoryData({ title: "", content: "", pet_id: "", paired_song_id: "" }); setShowStoryForm(true); }} className="w-full bg-white/90 backdrop-blur-sm rounded-3xl p-6 mb-6 border border-white/50 text-center hover:bg-sage/10 transition-all">
            <Plus size={24} className="mx-auto text-sage mb-2" />
            <span className="text-charcoal font-medium">Write a New Story 📖</span>
          </button>
          {showStoryForm && (
            <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-2xl font-semibold text-charcoal">{editingStory ? "Edit Story 📖" : "Write a Story 📖"}</h2>
                  <button onClick={() => { setShowStoryForm(false); setEditingStory(null); }} className="text-stone hover:text-charcoal p-2"><X size={24} /></button>
                </div>
                <div className="space-y-4">
                  <input type="text" value={storyData.title} onChange={(e) => setStoryData({ ...storyData, title: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="Story Title *" />
                  {pets.length > 0 && (
                    <select value={storyData.pet_id} onChange={(e) => setStoryData({ ...storyData, pet_id: e.target.value })} className="w-full h-12 border-2 border-gray-200 rounded-xl px-4 outline-none">
                      <option value="">About which pet?</option>
                      {pets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                  <textarea value={storyData.content} onChange={(e) => setStoryData({ ...storyData, content: e.target.value })} className="w-full h-48 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-3 outline-none resize-none" placeholder="Write your story... *" />
                  {songs.length > 0 && (
                    <div>
                      <label className="block text-charcoal font-medium mb-2 flex items-center gap-2">
                        <Music size={18} className="text-sage" />
                        Pair with a song (optional)
                      </label>
                      <select value={storyData.paired_song_id} onChange={(e) => setStoryData({ ...storyData, paired_song_id: e.target.value })} className="w-full h-12 border-2 border-gray-200 rounded-xl px-4 outline-none">
                        <option value="">Choose a song...</option>
                        {songs.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                      </select>
                    </div>
                  )}
                  <button onClick={handleSaveStory} disabled={loading} className="w-full bg-sage text-white py-4 rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50">{loading ? "Saving..." : (editingStory ? "Update Story 📖" : "Save Story 📖")}</button>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-4">
            {stories.filter(s => !s.prompt_used).map((s) => {
              const pairedSong = getSongById(s.paired_song_id);
              return (
                <div key={s.id} className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50 group">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-semibold text-charcoal mb-2">{s.title}</h3>
                      {s.pet_id && <p className="text-sage text-sm mb-3">🐾 About {getPetName(s.pet_id)}</p>}
                      <p className="text-charcoal whitespace-pre-wrap">{s.content}</p>
                      {pairedSong && pairedSong.audio_file && (
                        <MiniAudioPlayer audioFile={pairedSong.audio_file} songTitle={pairedSong.title} />
                      )}
                    </div>
                    <button onClick={() => handleEditStory(s)} className="opacity-0 group-hover:opacity-100 text-stone hover:text-sage transition-all p-2">
                      Edit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Milestones Tab */}
      {activeTab === "milestones" && (
        <div>
          <button onClick={() => setShowMilestoneForm(true)} className="w-full bg-white/90 backdrop-blur-sm rounded-3xl p-6 mb-6 border border-white/50 text-center hover:bg-terracotta/10 transition-all">
            <Award size={24} className="mx-auto text-terracotta mb-2" />
            <span className="text-charcoal font-medium">Add a Milestone 🎉</span>
          </button>
          {showMilestoneForm && (
            <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-2xl font-semibold text-charcoal">Add Milestone 🎉</h2>
                  <button onClick={() => setShowMilestoneForm(false)} className="text-stone hover:text-charcoal p-2"><X size={24} /></button>
                </div>
                <div className="space-y-4">
                  <select value={milestoneData.pet_id} onChange={(e) => setMilestoneData({ ...milestoneData, pet_id: e.target.value })} className="w-full h-12 border-2 border-gray-200 rounded-xl px-4 outline-none">
                    <option value="">Select Pet *</option>
                    {pets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input type="text" value={milestoneData.title} onChange={(e) => setMilestoneData({ ...milestoneData, title: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" placeholder="Milestone Title *" />
                  <input type="date" value={milestoneData.milestone_date} onChange={(e) => setMilestoneData({ ...milestoneData, milestone_date: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" />
                  <div className="grid grid-cols-3 gap-2">
                    {milestoneTypes.map((t) => (
                      <button key={t.value} type="button" onClick={() => setMilestoneData({ ...milestoneData, milestone_type: t.value })} className={`p-3 rounded-xl border-2 text-center transition-all ${milestoneData.milestone_type === t.value ? "border-terracotta bg-terracotta/10" : "border-gray-200"}`}>
                        <span className="text-2xl">{t.emoji}</span>
                      </button>
                    ))}
                  </div>
                  <textarea value={milestoneData.description} onChange={(e) => setMilestoneData({ ...milestoneData, description: e.target.value })} className="w-full h-20 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-2 outline-none resize-none" placeholder="Description (optional)" />
                  <button onClick={handleSaveMilestone} disabled={loading} className="w-full bg-terracotta text-white py-4 rounded-full font-medium hover:bg-terracotta-dark transition-all disabled:opacity-50">{loading ? "Saving..." : "Save Milestone 🎉"}</button>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-4">
            {milestones.map((m) => (
              <div key={m.id} className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50 flex items-start gap-4">
                <span className="text-4xl">{milestoneTypes.find(t => t.value === m.milestone_type)?.emoji || "⭐"}</span>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-semibold text-charcoal">{m.title}</h3>
                  <p className="text-sage text-sm">🐾 {getPetName(m.pet_id)}</p>
                  <p className="text-stone text-sm mt-1"><Calendar size={14} className="inline mr-1" />{formatDate(m.milestone_date)}</p>
                  {m.description && <p className="text-charcoal mt-2">{m.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guided Writing Modal */}
      {showGuidedModal && selectedPrompt && (
        <GuidedWritingModal
          prompt={selectedPrompt}
          onClose={() => { setShowGuidedModal(false); setSelectedPrompt(null); }}
          onSave={handleSaveGuidedEntry}
          pets={pets}
          songs={songs}
          loading={loading}
        />
      )}
    </div>
  );
};

export default JournalPage;
